/**
 * Trigger logic for the exit-intent / timed email signup popup.
 *
 * The Liquid side (snippets/exit-intent-signup-popup.liquid) already
 * decided whether the visitor is eligible to see the popup at all by
 * template (home, collection, product; never the dedicated signup
 * page). Everything else — trigger timing, the "seen before"
 * localStorage gate, and the "already has a discount applied" check —
 * lives here, deliberately client-side: this page can be served from
 * Shopify's full-page cache, so Liquid's `cart` and `request` objects
 * can reflect a *different* visitor's cache-generating request rather
 * than this one. Reading /cart.js and window.location here instead
 * always reflects this visitor's real, live state.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'libertytcg:emailPopupDismissedUntil';
  var SUPPRESS_DAYS = 7;
  var CONVERTED_SUPPRESS_DAYS = 30;
  var TIME_DELAY_MS = 20000;
  var SCROLL_THRESHOLD = 0.5;
  var EXIT_INTENT_ARM_DELAY_MS = 1500;

  function isSuppressed() {
    try {
      var until = window.localStorage.getItem(STORAGE_KEY);
      return !!until && Date.now() < Number(until);
    } catch (error) {
      return false;
    }
  }

  // days defaults to a plain dismiss (7); pass CONVERTED_SUPPRESS_DAYS for
  // a visitor who's already converted (existing discount, submitted email).
  function suppress(days) {
    try {
      var until = Date.now() + (days || SUPPRESS_DAYS) * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(STORAGE_KEY, String(until));
    } catch (error) {}
  }

  // Arrived via a discount link (e.g. Shopify's native `/discount/WELCOME5`
  // route) or with a `?discount=` param some marketing links use directly.
  function arrivedWithDiscountLink() {
    var path = window.location.pathname.toLowerCase();
    if (path.indexOf('/discount/') !== -1) return true;
    return /(?:^|[?&])discount=/i.test(window.location.search);
  }

  // Authoritative check: does the live cart already have a discount
  // applied? Covers both a code applied earlier in the session and one
  // Shopify's discount-link route just attached to this cart.
  function hasActiveCartDiscount() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        return !!(cart && cart.discount_codes && cart.discount_codes.length > 0);
      })
      .catch(function () {
        return false;
      });
  }

  function init() {
    var popup = document.getElementById('ExitIntentSignupPopup');
    if (!popup || typeof popup.showDialog !== 'function') return;

    // A server-rendered success/error state (see the Liquid file) opens
    // the dialog itself on load — nothing left for the trigger logic to do.
    var dialogEl = popup.querySelector('dialog');
    if (dialogEl && dialogEl.open) return;

    if (isSuppressed()) return;

    if (arrivedWithDiscountLink()) {
      suppress(CONVERTED_SUPPRESS_DAYS);
      return;
    }

    hasActiveCartDiscount().then(function (hasDiscount) {
      if (hasDiscount) {
        suppress(CONVERTED_SUPPRESS_DAYS);
        return;
      }
      armTriggers(popup);
    });
  }

  function armTriggers(popup) {
    var triggered = false;

    function cleanup() {
      window.clearTimeout(timerId);
      window.clearTimeout(armExitIntentId);
      document.removeEventListener('mouseout', handlePointerOut);
      window.removeEventListener('scroll', handleScroll);
    }

    function trigger() {
      if (triggered) return;
      triggered = true;
      cleanup();
      popup.showDialog();
    }

    function handlePointerOut(event) {
      // Cursor left the top of the viewport heading for the tab strip /
      // address bar — classic exit-intent signal. Desktop only; touch
      // devices don't produce a trailing mouseout like this.
      if (event.clientY <= 0 && !event.relatedTarget && !event.toElement) {
        trigger();
      }
    }

    function handleScroll() {
      var scrollTop = window.scrollY || document.documentElement.scrollTop;
      var scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      if (scrollTop / scrollable >= SCROLL_THRESHOLD) trigger();
    }

    var isDesktop = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    // Timed + scroll fallback always runs — the primary trigger on
    // mobile, a backup on desktop in case exit-intent never fires.
    var timerId = window.setTimeout(trigger, TIME_DELAY_MS);
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Exit-intent only makes sense with a real mouse; arm it after a
    // short delay so a stray cursor jump right at page load doesn't
    // fire it immediately.
    var armExitIntentId;
    if (isDesktop) {
      armExitIntentId = window.setTimeout(function () {
        document.addEventListener('mouseout', handlePointerOut);
      }, EXIT_INTENT_ARM_DELAY_MS);
    }

    // Wrapped, not passed directly: addEventListener would otherwise hand
    // suppress() the Event object as its `days` argument.
    popup.addEventListener('dialog:close', function () {
      suppress();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
